import FJPublishAPI from '@src/core/network/FJPublishAPI.ts';
import { SHARE_STATE, SOCIAL_KEY } from '@src/constants/FJShareSocialConstant.ts';
import FJPublishTarget from '@src/manager/publish/FJPublishTarget.ts';

class FJPublishManager {
    public blob: Blob | null = null;
    public originalData: any[] | null = null; // 视频原始的数据
    public duration: number = 0;
    public eventId: string = '';
    public subId: string = '';

    private _temporaryData: any = {};
    private readonly _publishTargets: FJPublishTarget[] = [];

    constructor() {
        this._publishTargets.push(new FJPublishTarget({ type: SOCIAL_KEY.YOUTUBE }));
        this._publishTargets.push(new FJPublishTarget({ type: SOCIAL_KEY.INSTAGRAM }));
        this._publishTargets.push(new FJPublishTarget({ type: SOCIAL_KEY.LINKEDIN }));
    }

    public clear() {
        this.blob = null;
        this.originalData = null;
        this._publishTargets.forEach(i => i.clear());
    }

    public clearTemporaryData() {
        this._temporaryData = {};
    }

    public saveTemporaryData() {
        const key = `${this.eventId}_${this.subId}`;
        this._publishTargets.forEach(tar => {
            const type = tar.type;
            if (!this._temporaryData[key]) {
                this._temporaryData[key] = {};
            }
            this._temporaryData[key][type] = tar.encode();
        });
    }

    public restoreData(): string[] {
        const key = `${this.eventId}_${this.subId}`;
        const data = this._temporaryData[key];
        const restoreTargets: string[] = [];
        if (data) {
            this._publishTargets.forEach(tar => {
                const type = tar.type;
                if (data[type]) {
                    tar.decode(data[type]);
                    restoreTargets.push(type);
                }
            });
        }

        return restoreTargets;
    }

    getPublishTarget(target: string) {
        return this._publishTargets.find(i => i.type === target);
    }

    public isEditContent() {
        return this._publishTargets.some(target => target.titleChanged || target.descriptionChanged);
    }

    public getPublishedTargets(): string[] {
        const publishedTargets: string[] = [];
        this._publishTargets.forEach(tar => {
            if (tar.status === SHARE_STATE.SHARE_SUCCESS) {
                publishedTargets.push(tar.type);
            }
        });
        return publishedTargets;
    }

    public getUnpublishedTargets(): string[] {
        const unpublishedTargets: string[] = [];
        this._publishTargets.forEach(tar => {
            if (tar.status !== SHARE_STATE.SHARE_SUCCESS) {
                unpublishedTargets.push(tar.type);
            }
        });
        return unpublishedTargets;
    }

    public async restorePublishedData() {
        const data: any[] = await FJPublishAPI.getPublishedData(this.eventId, this.subId).catch(e => {
            console.log(e);
            return null;
        });
        if (!data) {
            return;
        }

        data.forEach(d => {
            const target = this.getPublishTarget(d.social);
            if (target) {
                target.decode(d.data);
                target.status = SHARE_STATE.SHARE_SUCCESS;
            }
        });
    }

    public async savePublishedData(type: string) {
        const target = this.getPublishTarget(type);
        if (target) {
            FJPublishAPI.savePublishedData(this.eventId, this.subId, type, target.encode()).catch(() => {});
        }
    }

    public async getTextData(target: string | string[] = 'all', scope: string | string[] = 'all') {
        if (!this.originalData) {
            return null;
        }
        const { title, summary, subtitles }: any = this.originalData;
        let targetList: string[] = [target];
        if (target === 'all') {
            targetList = ['instagram', 'youtube-shorts', 'linkedin'];
        } else if (target === SOCIAL_KEY.YOUTUBE) {
            targetList = ['youtube-shorts'];
        } else if (typeof target !== 'string') {
            targetList = target.map(i => {
                if (i === SOCIAL_KEY.YOUTUBE) {
                    return 'youtube-shorts';
                } else {
                    return i;
                }
            });
        }
        const data = await FJPublishAPI.createSocialSharingDescription(
            this.eventId,
            this.subId,
            title,
            summary,
            subtitles,
            targetList,
            scope,
        ).catch(e => {
            throw e;
        });

        if (data?.platforms) {
            data.platforms.forEach(item => {
                let key = item.platform;
                if (key === 'youtube-shorts') {
                    key = SOCIAL_KEY.YOUTUBE;
                }
                const publishTarget = this.getPublishTarget(key);
                if (publishTarget) {
                    if (item.title) {
                        publishTarget.title = item.title;
                    }
                    if (item.caption) {
                        let desc = item.caption;
                        if (item.hashtags && item.caption.length !== 0) {
                            desc += '\n\n';
                            item.hashtags.forEach(tag => {
                                desc += '#' + tag + ' ';
                            });
                        }
                        publishTarget.description = desc;
                    }
                    if (item.hashtags) {
                        publishTarget.tags = item.hashtags;
                    }
                }
            });
        }
    }
}

const manager = new FJPublishManager();
export default manager;
